import { deepMerge, Text } from '@mantine/core';
import {
  OnChangeFn,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/table-core';
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PfTable from '../pf-table/pf-table';
import { useRowSelection } from '../pf-table/use-row-selection';
import StatusCell from '../status-cell/status-cell';
import StatusColumnFilter from '../status-column-filter/status-column-filter';

import { dataColStates } from './col-states';
import ColumnSegmentedControl from './components/column-segmented-control/column-segmented-control';

import {
  FORM_STATUS,
  formatDate,
  Insurance,
  useFiltersTableOptions,
  useInsurances,
} from '@data-access';

interface InsurancesTableProps {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

export function InsurancesTable({
  rowSelection,
  setRowSelection,
}: InsurancesTableProps) {
  const { t } = useTranslation();
  const filtersTableOptions = useFiltersTableOptions<Insurance>('insurances');
  const insurances = useInsurances();

  const rowSelectionProps = useRowSelection<Insurance>({
    data: insurances.data?.results || [],
    rowSelection,
    setRowSelection,
  });

  const tableColumns = useMemo<MRT_ColumnDef<Insurance>[]>(
    () => [
      {
        accessorKey: 'field.thumbnail',
        enableEditing: false,
        enableColumnFilter: false,
        header: '',
        enableSorting: false,
        enableColumnActions: false,
        size: 32,
        Cell: ({ cell, row }) => {
          const thumb = cell.getValue<string>();
          if (thumb) {
            return <img alt="thumbnail" src={thumb} width="32" />;
          }
        },
      },
      {
        accessorKey: 'field.name',
        header: t('fields.col.field-name'),
      },
      {
        accessorKey: 'organization.name',
        header: t('fields.col.organization-name'),
      },
      {
        accessorKey: 'field.farm.name',
        header: t('fields.col.farm-name'),
      },
      {
        accessorKey: 'seed_date',
        header: t('common.seed_date'),
        Cell: ({ cell }) => {
          const date = cell.getValue<string>();
          return (
            <Text>
              {date
                ? formatDate({
                    date,
                    format: 'display',
                  })
                : '-'}
            </Text>
          );
        },
        filterVariant: 'date-range',
      },
      {
        accessorKey: 'is_harvested',
        header: t('table.columns.harvestedFarm'),
        Cell: ({ cell }) => {
          const value = cell.getValue<boolean>();
          return t(value ? 'common.yes' : 'common.no');
        },
        minSize: 250,
        filterVariant: 'checkbox',
      },
      {
        accessorKey: 'department.name',
        header: t('common.department'),
      },
      {
        accessorKey: 'max_LAI',
        Cell: ({ cell }) => cell.getValue<number>() ?? '-',
        header: t('common.properties.max_lai'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'rain_seeding',
        size: 250,
        Cell: ({ cell }) =>
          cell.getValue<number>() ? `${cell.getValue<number>()} mm` : '-',
        header: t('common.properties.rain_seeding'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'rain_cycle',
        size: 270,
        Cell: ({ cell }) =>
          cell.getValue<number>() ? `${cell.getValue<number>()} mm` : '-',
        header: t('common.properties.rain_cycle'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'rain_harvest',
        size: 250,
        Cell: ({ cell }) =>
          cell.getValue<number>() ? `${cell.getValue<number>()} mm` : '-',
        header: t('common.properties.rain_harvest'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'status',
        header: t('table.columns.status'),
        maxSize: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<FORM_STATUS>();

          return <StatusCell status={status} />;
        },
        Filter: ({ column, table }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<Insurance> = deepMerge<
    MRT_TableOptions<Insurance>
  >(
    {
      columns: tableColumns,
      data: insurances.data?.results || [],

      rowCount: insurances.data?.count || 0,

      mantinePaginationProps: {
        showRowsPerPage: false,
      },

      state: {
        showLoadingOverlay: insurances.isLoading,
        rowSelection,
        showProgressBars: insurances.isFetching,
      },

      initialState: {
        columnVisibility: dataColStates as VisibilityState,
        showColumnFilters: true,
      },
      mantinePaperProps: {
        h: '100%',
      },
      renderTopToolbarCustomActions: ({ table }) => (
        <ColumnSegmentedControl table={table as any} />
      ),
      ...rowSelectionProps,
    },
    filtersTableOptions,
  );

  return <PfTable<Insurance> options={tableOptions} />;
}

export default InsurancesTable;
